import { useMemo, useState, type CSSProperties } from "react";
import { ChevronRight, CircleDot, Code2, Network, Search, Sparkles, TrendingUp } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Cell, LabelList, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useProjectDesignationOptions } from "../../../Hooks/useProjectDesignationOptions.js";

import { formatCurrency } from "../../utils/currency.ts";
import { getDesignationDisplayLabel } from "../../utils/designation-display.ts";
import { PhaseBreakdownCard } from "./phase-breakdown-card.tsx";
import type { ContributorTrackingReport, ProjectPhaseDailyActivity, ProjectPhaseTracking, ProjectTrackingReport } from "./types.ts";
import styles from "./workstream-tracking-page.module.css";

type Props = {
  project: ProjectTrackingReport;
  contributors: ContributorTrackingReport[];
  currency: string;
  onViewTimeline: () => void;
};

export type ProjectPhaseView = {
  key: string;
  name: string;
  planned: number;
  planningMode: "PLANNED" | "OPEN_ENDED" | "PROJECT_ALIGNED";
  hasHourPlan: boolean;
  plannedWeeks: number;
  utilised: number | null;
  commercial: number | null;
  internal: number | null;
  start: string | null;
  end: string | null;
  actualStart: string | null;
  actualEnd: string | null;
  dailyActivity: ProjectPhaseDailyActivity[];
};

type DesignationCost = {
  designation: string;
  utilised: number;
  commercialCost: number;
  internalCost: number;
  commercialComplete: boolean;
  internalComplete: boolean;
};

const designationColors = ["#31a85b", "#da2921", "#7439ee", "#3f68e8", "#cf7c00", "#16a0b5", "#a14faa", "#64748b"];
const phaseColors = ["#456de8", "#7439ee", "#cc7600", "#3ca561", "#4267e8", "#713ee4"];
const number = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 1 });
const hours = (value: number) => `${number.format(value)}h`;
const percentage = (actual: number, planned: number) => planned > 0 ? (actual / planned) * 100 : 0;
const phaseActual = (item?: ProjectPhaseTracking): number | null => {
  const value = item?.utilisedHours ?? item?.utilizedHours ?? item?.actualHours;
  return value == null ? null : Number(value);
};
const phasePlanned = (item?: ProjectPhaseTracking) => Number(item?.plannedHours ?? item?.allocatedHours ?? 0);

export function getProjectPhases(project: ProjectTrackingReport): ProjectPhaseView[] {
  const responsePhases = project.phaseBreakdown ?? project.phases ?? [];
  return responsePhases.map((phase, index) => {
    const planningMode = phase.planningMode ?? "PLANNED";
    return ({
    key: String(phase.phaseId ?? phase.phase ?? phase.phaseName ?? index),
    name: phase.phaseName || phase.phase || `Phase ${index + 1}`,
    planned: phasePlanned(phase),
    planningMode,
    hasHourPlan: planningMode === "PLANNED",
    plannedWeeks: 0,
    utilised: phaseActual(phase),
    commercial: phase.commercialBurn ?? phase.commercialValue ?? null,
    internal: phase.internalCost ?? null,
    start: phase.startDate ?? null,
    end: phase.endDate ?? null,
    actualStart: phase.actualStartDate ?? null,
    actualEnd: phase.actualEndDate ?? null,
    dailyActivity: phase.dailyActivity ?? [],
    });
  });
}

export function ProjectUtilisationOverview({ project, contributors, currency, onViewTimeline }: Props) {
  useProjectDesignationOptions();
  const [chartMode, setChartMode] = useState<"hours" | "cost">("hours");
  const phases = getProjectPhases(project);
  const planned = Number(project.totalProjectHours ?? 0);
  const utilised = Number(project.actualHours ?? 0);
  const utilisation = percentage(utilised, planned);
  const utilisationTone = utilisation > 100 ? "#da2921" : utilisation >= 80 ? "#e7aa00" : "#31a85b";
  const designations = useMemo(() => {
    const grouped = new Map<string, DesignationCost>();
    contributors.forEach((item) => {
      const key = item.designation || "UNASSIGNED";
      const row = grouped.get(key) ?? { designation: key, utilised: 0, commercialCost: 0, internalCost: 0, commercialComplete: true, internalComplete: true };
      const actual = Number(item.actualHours ?? 0);
      const commercialRate = Number(item.commercialRate ?? 0);
      const internalRate = Number(item.internalRate ?? 0);
      row.utilised += actual;
      if (actual > 0) {
        row.commercialComplete &&= item.commercialRateConfigured !== false && item.commercialRate != null;
        row.internalComplete &&= item.internalRateConfigured !== false && item.internalRate != null;
      }
      row.commercialCost += item.commercialValue == null ? actual * commercialRate : Number(item.commercialValue);
      row.internalCost += item.internalCost == null ? actual * internalRate : Number(item.internalCost);
      grouped.set(key, row);
    });
    return [...grouped.values()].sort((left, right) => right.utilised - left.utilised);
  }, [contributors]);
  const totalCommercial = Number(project.commercialBurn ?? designations.reduce((sum, item) => sum + item.commercialCost, 0));
  const totalInternal = Number(project.internalCost ?? designations.reduce((sum, item) => sum + item.internalCost, 0));
  const margin = totalCommercial - totalInternal;
  const marginPercent = totalCommercial > 0 ? (margin / totalCommercial) * 100 : 0;
  const blendedCommercialRate = utilised > 0 ? totalCommercial / utilised : 0;
  const blendedInternalRate = utilised > 0 ? totalInternal / utilised : 0;
  const phaseChartData = phases.map((phase) => {
    const phaseContributorRows = (project.phaseContributors ?? []).filter((item) =>
      String(item.phaseId) === phase.key || item.phaseName === phase.name,
    );
    return {
      ...phase,
      plannedChart: phase.hasHourPlan ? phase.planned : null,
      commercial: phase.commercial ?? (phaseContributorRows.length
        ? phaseContributorRows.reduce((sum, item) => sum + Number(item.commercialBurn ?? 0), 0)
        : Number(phase.utilised ?? 0) * blendedCommercialRate),
      internal: phase.internal ?? (phaseContributorRows.length
        ? phaseContributorRows.reduce((sum, item) => sum + Number(item.internalCost ?? 0), 0)
        : Number(phase.utilised ?? 0) * blendedInternalRate),
    };
  });
  const designationHours = designations.reduce((sum, item) => sum + item.utilised, 0);
  const designationRows = designations.map((item, index) => ({
    ...item,
    color: designationColors[index % designationColors.length],
    label: getDesignationDisplayLabel(item.designation),
    share: designationHours > 0 ? (item.utilised / designationHours) * 100 : 0,
  }));
  const maxDesignationCost = Math.max(1, ...designationRows.map((item) => Math.max(item.commercialCost, item.internalCost)));
  const donut = [
    { name: "Utilised", value: Math.min(utilised, planned || utilised) },
    { name: "Remaining", value: Math.max(planned - utilised, 0) },
  ];

  return <section className={styles.trackingVisuals}>
    <div className={styles.trackingMainGrid}>
      <div className={styles.trackingLeftStack}>
        <article className={`${styles.compactInsightCard} ${styles.compactUtilisation}`}>
          <h2>Utilisation Overview</h2>
          <div className={styles.compactUtilisationBody}>
            <div className={styles.compactDonut}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={donut} dataKey="value" innerRadius="69%" outerRadius="88%" startAngle={90} endAngle={-270} cornerRadius={7} stroke="none">
                    <Cell fill={utilisationTone} /><Cell fill="#e9edf2" />
                  </Pie>
                  <Tooltip content={<UtilisationTooltip utilised={utilised} planned={planned} utilisation={utilisation} />} />
                </PieChart>
              </ResponsiveContainer>
              <div><strong>{number.format(utilisation)}%</strong><small>Utilised</small></div>
            </div>
            <div className={styles.compactUtilisationStats}>
              <span><i style={{ background: utilisationTone }} />Utilised <strong>{hours(utilised)}</strong><b>{number.format(utilisation)}%</b></span>
              <span><i />Planned <strong>{hours(planned)}</strong></span>
              <div className={utilisation > 100 ? styles.compactTrackRisk : styles.compactTrackGood}><TrendingUp />{utilisation > 100 ? "Needs attention" : "On track"}</div>
            </div>
          </div>
        </article>

        <article className={`${styles.compactInsightCard} ${styles.compactDesignationCard}`}>
          <header><div><h2>Hours by Designation</h2><p>Utilised hours across all roles</p></div><span>{hours(designationHours)} total</span></header>
          <div className={styles.compactDesignationRows}>
            {designationRows.length ? designationRows.map((item) => <div className={styles.compactDesignationRow} key={item.designation} style={{ "--designation-color": item.color } as CSSProperties}>
              <i /><span title={item.label}>{item.label}</span><em><b style={{ width: `${Math.min(100, item.share)}%` }} /></em><strong>{hours(item.utilised)}</strong><small>{number.format(item.share)}%</small>
            </div>) : <p className={styles.compactEmpty}>No utilised designation hours available.</p>}
          </div>
        </article>
      </div>

      <article className={`${styles.compactInsightCard} ${styles.compactPhaseStatus}`}>
        <header><h2>Phase Status</h2><span>Used / Planned</span><span>%</span></header>
        <div>{phases.map((phase, index) => <CompactPhaseStatus key={phase.key} phase={phase} index={index} />)}</div>
        <button type="button" className={styles.compactTimelineLink} onClick={onViewTimeline}>View Phase Timeline <ChevronRight /></button>
      </article>
    </div>

    <div className={styles.trackingBottomGrid}>
      <article className={`${styles.compactInsightCard} ${styles.compactPhaseChart}`}>
        <header><div><h2>{chartMode === "hours" ? "Phase-wise Planned vs Utilised" : "Phase-wise Commercial vs Internal"}</h2><p>{chartMode === "hours" ? "Hours consumed per phase against plan" : "Burn by phase — commercial and internal cost"}</p></div><div className={styles.chartMode}><button type="button" aria-pressed={chartMode === "hours"} onClick={() => setChartMode("hours")}>Hours</button><button type="button" aria-pressed={chartMode === "cost"} onClick={() => setChartMode("cost")}>Cost</button></div></header>
        <div className={styles.compactPhaseChartPlot}>
          <div className={styles.compactPhaseChartCanvas}><ResponsiveContainer width="100%" height="100%" debounce={80}>
            <BarChart data={phaseChartData} margin={{ top: 30, right: 12, left: -14, bottom: 8 }} barCategoryGap="28%">
              <CartesianGrid vertical={false} stroke="#dfe5ed" strokeDasharray="4 4" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#52617a" }} interval={0} axisLine={{ stroke: "#cfd7e3" }} tickLine={false} />
              <YAxis tickFormatter={chartMode === "cost" ? (value) => compactCurrency(Number(value), currency) : undefined} tick={{ fontSize: 10, fill: "#8793a8" }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip cursor={{ fill: "rgba(69, 109, 232, .035)" }} content={<PhaseChartTooltip mode={chartMode} currency={currency} />} />
              {chartMode === "hours" ? <>
                <Bar dataKey="plannedChart" name="Planned Hours" fill="#bad3fa" radius={[4, 4, 0, 0]} maxBarSize={34}><LabelList dataKey="plannedChart" position="top" formatter={(value: unknown) => Number(value ?? 0) > 0 ? hours(Number(value)) : ""} fill="#52617a" fontSize={10} /></Bar>
                <Bar dataKey="utilised" name="Utilised Hours" fill="#4e7df1" radius={[4, 4, 0, 0]} maxBarSize={34}>{phaseChartData.map((phase) => <Cell key={phase.key} fill={phase.hasHourPlan && phase.planned > 0 && Number(phase.utilised ?? 0) > phase.planned ? "#da2921" : "#4e7df1"} />)}<LabelList dataKey="utilised" position="top" formatter={(value: unknown) => value == null || Number(value) === 0 ? "" : hours(Number(value))} fill="#3f68e8" fontSize={10} /></Bar>
              </> : <>
                <Bar dataKey="commercial" name="Commercial" fill="#bad3fa" radius={[4, 4, 0, 0]} maxBarSize={34}><LabelList dataKey="commercial" position="top" formatter={(value: unknown) => Number(value ?? 0) > 0 ? compactCurrency(Number(value), currency) : ""} fill="#52617a" fontSize={10} /></Bar>
                <Bar dataKey="internal" name="Internal" fill="#4264e8" radius={[4, 4, 0, 0]} maxBarSize={34}><LabelList dataKey="internal" position="top" formatter={(value: unknown) => Number(value ?? 0) > 0 ? compactCurrency(Number(value), currency) : ""} fill="#365bea" fontSize={10} /></Bar>
              </>}
            </BarChart>
          </ResponsiveContainer></div>
          <div className={styles.compactPhaseChartLegend}>
            <span><i />{chartMode === "hours" ? "Planned Hours" : "Commercial"}</span>
            <span><i />{chartMode === "hours" ? "Utilised Hours" : "Internal"}</span>
          </div>
        </div>
      </article>

      <article className={`${styles.compactInsightCard} ${styles.compactBurnCard}`}>
        <header><h2>Burn &amp; Margin</h2><span className={marginPercent >= 0 ? styles.positiveMargin : styles.negativeMargin}>{number.format(marginPercent)}% margin</span></header>
        <div className={styles.burnSummary}>
          <div><span>Commercial</span><strong>{formatCurrency(totalCommercial, currency, 0)}</strong></div>
          <div title={project.salesCommissionIncluded ? `Includes ${formatCurrency(project.salesCommissionAmount ?? 0, currency, 0)} sales commission` : undefined}>
            <span>Internal{project.salesCommissionIncluded ? " + commission" : ""}</span>
            <strong>{formatCurrency(totalInternal, currency, 0)}</strong>
          </div>
          <div><span>Margin</span><strong>{formatCurrency(margin, currency, 0)}</strong></div>
        </div>
        <span className={styles.burnByLabel}>By Designation</span>
        <div className={styles.burnRows}>
          {designationRows.length ? designationRows.map((item) => <div className={styles.burnRow} key={item.designation} tabIndex={0}>
            <span>{item.label}</span><strong>{formatCurrency(item.commercialCost, currency, 0)}</strong>
            <div className={styles.burnTrack}><i style={{ width: `${(item.commercialCost / maxDesignationCost) * 100}%` }} /><em style={{ width: `${(item.internalCost / maxDesignationCost) * 100}%` }} /></div>
            <BurnTooltip item={item} currency={currency} />
          </div>) : <p className={styles.compactEmpty}>No designation burn data available.</p>}
        </div>
        <div className={styles.burnLegend}><span><i />Commercial</span><span><i />Internal</span></div>
      </article>
    </div>
    <PhaseBreakdownCard project={project} currency={currency} />
  </section>;
}

function CompactPhaseStatus({ phase, index }: { phase: ProjectPhaseView; index: number }) {
  const value = phase.utilised ?? 0;
  const percent = percentage(value, phase.planned);
  const hasPlan = phase.hasHourPlan;
  const hasPositivePlan = hasPlan && phase.planned > 0;
  const label = !hasPlan ? "Open-ended" : value === 0 ? "Not Started" : hasPositivePlan && percent > 100 ? "Over Allocated" : hasPositivePlan && percent === 100 ? "Completed" : "In Progress";
  const tone = hasPositivePlan && percent > 100 ? "#da2921" : hasPositivePlan && percent >= 80 ? "#e7aa00" : phaseColors[index % phaseColors.length];
  const icons = [<Search key="discovery" />, <Network key="architecture" />, <Code2 key="development" />, <CircleDot key="uat" />, <Sparkles key="research" />, <CircleDot key="governance" />];
  return <div className={`${styles.compactPhaseRow} ${hasPositivePlan && percent > 100 ? styles.compactPhaseOver : ""}`}>
    <span className={styles.compactPhaseIcon} style={{ "--phase-color": phaseColors[index % phaseColors.length] } as CSSProperties}>{icons[index % icons.length]}</span>
    <div className={styles.compactPhaseName}><strong>{phase.name}</strong><small style={{ color: tone }}>{label}</small></div>
    <i className={styles.compactPhaseProgress}><em style={{ width: `${hasPositivePlan ? Math.min(100, percent) : 0}%`, background: tone }} /></i>
    <span className={styles.compactPhaseValues}><b>{hours(value)}</b>{hasPlan ? ` / ${hours(phase.planned)}` : " / N/A"}</span>
    <strong className={styles.compactPhasePercent} style={{ color: hasPositivePlan ? tone : "#667085" }}>{hasPositivePlan ? `${number.format(percent)}%` : "N/A"}</strong>
  </div>;
}

function UtilisationTooltip({ active, utilised, planned, utilisation }: { active?: boolean; utilised: number; planned: number; utilisation: number }) {
  if (!active) return null;
  return <div className={styles.compactTooltip}><strong>Project utilisation</strong><span>Planned hours <b>{hours(planned)}</b></span><span>Utilised hours <b>{hours(utilised)}</b></span><span>Remaining hours <b>{hours(Math.max(planned - utilised, 0))}</b></span><span>Utilisation <b>{number.format(utilisation)}%</b></span></div>;
}

function PhaseChartTooltip({ active, payload, mode, currency }: { active?: boolean; payload?: Array<{ payload?: ProjectPhaseView & { commercial: number; internal: number } }>; mode: "hours" | "cost"; currency: string }) {
  const phase = payload?.[0]?.payload;
  if (!active || !phase) return null;
  if (mode === "cost") {
    const phaseMargin = phase.commercial - phase.internal;
    return <div className={`${styles.compactTooltip} ${styles.darkChartTooltip}`}><strong>{phase.name}</strong><span><i className={styles.tooltipCommercial} />Commercial <b>{formatCurrency(phase.commercial, currency, 0)}</b></span><span><i className={styles.tooltipInternal} />Internal <b>{formatCurrency(phase.internal, currency, 0)}</b></span><span><i className={styles.tooltipMargin} />Margin <b>{formatCurrency(phaseMargin, currency, 0)}</b></span></div>;
  }
  const phaseUtilised = Number(phase.utilised ?? 0);
  const usage = percentage(phaseUtilised, phase.planned);
  const usageClass = phase.hasHourPlan && phase.planned > 0 && usage > 100 ? styles.tooltipDanger : styles.tooltipUsage;
  return <div className={`${styles.compactTooltip} ${styles.darkChartTooltip}`}><strong>{phase.name}</strong><span><i className={styles.tooltipPlanned} />Planned <b>{phase.hasHourPlan ? hours(phase.planned) : "N/A"}</b></span><span><i className={usageClass} />Utilised <b>{hours(phaseUtilised)}</b></span><span><i className={usageClass} />Utilization <b>{phase.hasHourPlan && phase.planned > 0 ? `${number.format(usage)}%` : "N/A"}</b></span></div>;
}

function BurnTooltip({ item, currency }: { item: DesignationCost & { label: string; share: number; color: string }; currency: string }) {
  const itemMargin = item.commercialCost - item.internalCost;
  const itemMarginPercent = item.commercialCost > 0 ? (itemMargin / item.commercialCost) * 100 : 0;
  return <div className={styles.burnHoverTooltip} aria-hidden="true"><strong>{item.label}</strong><span><i className={styles.tooltipCommercial} />Commercial <b>{formatCurrency(item.commercialCost, currency, 0)}</b></span><span><i className={styles.tooltipInternal} />Internal <b>{formatCurrency(item.internalCost, currency, 0)}</b></span><em><span>Margin</span><b>{formatCurrency(itemMargin, currency, 0)} ({number.format(itemMarginPercent)}%)</b></em></div>;
}

function compactCurrency(value: number, currency: string) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency, notation: "compact", maximumFractionDigits: 1 }).format(value);
}
